import { gql } from '@apollo/client';
import { ErrorIcon } from '@assets/icons';
import { Select, TransferList } from '@components/form';
import { useModal, type UseModalProps } from '@components/modal';
import { Typography } from '@components/typography';
import {
  useAddEditorMembersMutation,
  useEditEditorMutation,
  useGetManageWorkspaceMembersDataQuery,
  useGetMembersQuery,
  useGetServerMembersDataQuery,
  useRemoveEditorMembersMutation,
} from '@generated/graphql';
import { Box, MenuItem, Stack, alpha, type SelectChangeEvent } from '@mui/material';
import posthog from 'posthog-js';

gql`
  query GetServerMembersData($editorId: String) {
    getEditors(editorId: $editorId) {
      editorId
      workspaceId
      createdBy
      users
      visibility
    }
  }
`;

type ServerMember = {
  email: string;
  name: string;
};

type ManageServerMembersModalProps = {
  organizationId: string;
  editorId: string;
  onChange?: VoidFunction;
};

const ManageServerMembersModal = ({
  organizationId,
  editorId,
  onChange,
}: ManageServerMembersModalProps) => {
  const [addEditorMembers] = useAddEditorMembersMutation();
  const [removeEditorMembers] = useRemoveEditorMembersMutation();
  const [editEditor] = useEditEditorMutation();

  const {
    data: orgMembersData,
    loading: availableLoading,
    refetch: refetchOrgMembers,
  } = useGetMembersQuery({
    variables: { organizationId },
    skip: !organizationId,
  });

  const {
    data: serverData,
    loading: serverLoading,
    refetch: refetchServerData,
  } = useGetServerMembersDataQuery({
    variables: { editorId },
    skip: !editorId,
  });

  const serverOwner = serverData?.getEditors?.[0]?.createdBy;
  const serverUserEmails = serverData?.getEditors?.[0]?.users || [];
  const serverWorkspaceId = serverData?.getEditors?.[0]?.workspaceId;
  const currentVisibility = serverData?.getEditors?.[0]?.visibility || 'private';

  // Fetch workspace members data (users, owner, visibility)
  const { data: workspaceMembersData } = useGetManageWorkspaceMembersDataQuery({
    variables: { workspaceId: serverWorkspaceId },
    skip: !serverWorkspaceId,
  });
  const workspaceUsers = workspaceMembersData?.getWorkspaces?.[0]?.users || [];
  const workspaceOwner = workspaceMembersData?.getWorkspaces?.[0]?.createdBy;
  const workspaceVisibility = workspaceMembersData?.getWorkspaces?.[0]?.visibility;

  // All workspace member emails (including owner). Public workspaces give all org members access.
  const allWorkspaceMemberEmails =
    workspaceVisibility === 'public'
      ? (orgMembersData?.getMembers || []).map((m) => m.email)
      : Array.from(new Set([...workspaceUsers, ...(workspaceOwner ? [workspaceOwner] : [])]));

  const allMembers = orgMembersData?.getMembers || [];
  const workspaceMembers = allMembers.filter((m) => allWorkspaceMemberEmails.includes(m.email));
  const availableMembers = serverOwner
    ? workspaceMembers.filter((m) => m.email !== serverOwner)
    : workspaceMembers;

  // Map current server users to member objects, excluding the owner
  const currentMembers =
    !serverUserEmails.length || !availableMembers.length
      ? []
      : serverUserEmails
          .filter((email) => email !== serverOwner)
          .map((email) => availableMembers.find((m) => m.email === email))
          .filter(Boolean);

  // Map workspace member emails to member objects for read-only display, excluding server owner
  const workspaceMemberObjects =
    !allWorkspaceMemberEmails.length || !allMembers.length
      ? []
      : allWorkspaceMemberEmails
          .filter((email) => email !== serverOwner)
          .map((email) => allMembers.find((m) => m.email === email))
          .filter(Boolean);

  const handleRefetch = () => {
    void refetchOrgMembers();
    void refetchServerData();
  };

  const handleVisibilityChange = async (event: SelectChangeEvent<string>) => {
    const newVisibility = event.target.value;
    await editEditor({
      variables: { editorId, visibility: newVisibility },
      onCompleted: () => {
        posthog.capture('agent-studio:user_change-editor-visibility', {
          editorId,
          visibility: newVisibility,
        });
        handleRefetch();
        onChange?.();
      },
    });
  };

  const handleAddMembers = async (members: ServerMember[]) => {
    const membersToAdd = members.map((m) => m.email);
    posthog.capture('agent-studio:user_add-editor-members_submitted', {
      editorId,
      emails: membersToAdd,
    });
    await addEditorMembers({
      variables: { editorId, emails: membersToAdd },
      onCompleted: () => {
        posthog.capture('agent-studio:user_add-editor-members_completed', {
          editorId,
          emails: membersToAdd,
        });
        handleRefetch();
        onChange?.();
      },
    });
  };

  const handleRemoveMembers = async (members: ServerMember[]) => {
    const membersToRemove = members.map((m) => m.email);
    posthog.capture('agent-studio:user_remove-editor-members_submitted', {
      editorId,
      emails: membersToRemove,
    });
    await removeEditorMembers({
      variables: { editorId, emails: membersToRemove },
      onCompleted: () => {
        posthog.capture('agent-studio:user_remove-editor-members_completed', {
          editorId,
          emails: membersToRemove,
        });
        handleRefetch();
        onChange?.();
      },
    });
  };

  const isPrivate = currentVisibility === 'private';

  return (
    <Stack gap={5}>
      <Stack gap={1}>
        <Typography variant="body2" color="text.paper">
          Give other workspace members access to this server.
        </Typography>
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 1,
            px: 1.5,
            py: 0.75,
            borderRadius: '100px',
            border: '1px solid',
            borderColor: 'warning.main',
            color: 'warning.main',
            backgroundColor: (theme) => alpha(theme.palette.warning.main, 0.08),
          }}
        >
          <ErrorIcon size={11} style={{ flexShrink: 0 }} />
          <Typography variant="caption2" sx={{ color: 'inherit' }}>
            Members with access will be able to access confidential information on the server.
          </Typography>
        </Box>
      </Stack>

      <Select
        label="Visibility"
        labelInfo="Workspace servers inherit access from the workspace. Private servers are only accessible to you and invited members."
        value={currentVisibility}
        onChange={handleVisibilityChange}
        fullWidth={false}
        containerProps={{ sx: { maxWidth: 240 } }}
      >
        <MenuItem value="workspace">Workspace</MenuItem>
        <MenuItem value="private">Private</MenuItem>
      </Select>

      {isPrivate ? (
        <TransferList<ServerMember, unknown>
          availableLoading={availableLoading}
          availableItems={availableMembers}
          selectedItems={currentMembers}
          selectedLoading={serverLoading}
          selectedTitle="Shared with"
          availableTitle="No access"
          emptySelectedText="Not shared with anyone"
          emptyAvailableText="Everyone has access"
          label={(member) => `${member.name} <${member.email}>`}
          resourceTitle="members"
          resourceIdKey="email"
          onAdd={handleAddMembers}
          onRemove={handleRemoveMembers}
        />
      ) : (
        <TransferList<ServerMember, unknown>
          editable={false}
          selectedTitle="Shared with"
          emptySelectedText="Not shared with anyone"
          availableLoading={false}
          availableItems={[]}
          selectedItems={workspaceMemberObjects}
          selectedLoading={false}
          label={(member) => `${member.name} <${member.email}>`}
          resourceTitle="members"
          resourceIdKey="email"
          onAdd={async () => {}}
          onRemove={async () => {}}
        />
      )}
    </Stack>
  );
};

export const useManageServerMembersModal = ({
  componentProps,
  modalProps,
}: UseModalProps<typeof ManageServerMembersModal>) => {
  return useModal({
    component: ManageServerMembersModal,
    componentProps,
    modalProps: {
      title: 'Share server',
      sx: {
        width: '90vw',
        maxWidth: '1000px',
        maxHeight: '85vh',
        overflow: 'auto',
      },
      ...modalProps,
    },
  });
};
